import { PlusIcon } from "@heroicons/react/solid"
import { Breadcrumbs } from "components/breadcrumbs"
import Button from "components/button"
import { Link } from "react-router-dom"
import { AdminAccountCard, AdminAccountTable, Filter } from "./components"
import { useTable, useUpdate } from "@refinedev/core"
import { useMemo } from "react"
import Pagination from "components/pagination"
import { FullScreenLoading } from "components/fullScreenLoading"
import { IAdminAccountItem } from "interfaces"
import toast from "react-hot-toast"
import { isHaveActionPermission } from "utills/helpers"

export const AdminAccountList: React.FC = () => {
  const { mutate: updateMutation, isLoading: updateMutationIsLoading } = useUpdate();

  const {
    tableQuery,
    filters,
    setFilters,
  } = useTable<IAdminAccountItem>({
    resource: "admin-account/account",
    syncWithLocation: true,
    pagination: {
      mode: "off",
    },
    meta: {
      resource: "accounts",
    },
  });

  const currentFilterValues = useMemo(() => {
    const logicalFilters = filters.flatMap((item) =>
      "field" in item ? item : []
    );

    return {
      q: logicalFilters.find((item) => item.field === "q")?.value || "",
      is_active: logicalFilters.find((item) => item.field === "is_active")?.value || "",
      role_id: logicalFilters.find((item) => item.field === "role_id")?.value || "",
    };
  }, [filters]);

  const _toggleActive = (id: string, newActiveStatus: boolean) => {
    if (!isHaveActionPermission("account", "admin", "edit")) return;
    if (window.confirm(`Are you sure you want to ${newActiveStatus ? "activate": "deactivate"} this account?`)) {
      updateMutation({
        resource: "admin-account/account",
        id,
        meta: {
          URLSuffix: `active/${newActiveStatus ? "true" : "false"}`,
          ignoreResourceWrapper: true
        },
        values: {},
      },
      {
        onSuccess: () => {
          toast.success("Update successfully");
        },
      });
    }
  };

  const _handlePageChanged = (page: number) => {
    const updatedFilters = [...filters].flatMap(item => "field" in item ? item: []);
    const pageIndex = updatedFilters.findIndex(filter => filter.field === "page");
    if (pageIndex < 0) {
      updatedFilters.push({field: "page", operator: "eq", value: page})
    } else {
      updatedFilters[pageIndex].value = page;
    }

    setFilters(updatedFilters);
  }
  
  return (
    <div className="wrapper">
      <Breadcrumbs
        {...(isHaveActionPermission("account", "admin", "create") && {
          additionalLabel: (
            <Link to={"/admin-accounts/accounts/create"}>
              <Button>
                <span className="sr-only">Create new account</span>
                <PlusIcon className="w-4 h-4 text-white" />{" "}
                <span className="hidden sm:block sm:ml-2">
                  Create new account
                </span>
              </Button>
            </Link>
          ),
        })}
      />
      <div className="box-container">
        <Filter
          currentFilterValues={currentFilterValues}
          setFilters={setFilters}
        />
      </div>
      <div className="box-container">
        {!tableQuery.data?.data?.length &&
          !tableQuery.isFetching && (
            <div className="text-center">
              No data
            </div>
          )}
        {tableQuery?.data?.data && tableQuery?.data?.data.length > 0 ? (
          <>
            <div className="sm:hidden">
              <AdminAccountCard
                data={tableQuery?.data?.data}
                pageSize={tableQuery?.data?.pageSize}
                currentPage={tableQuery?.data?.paging.current_page}
                _toggleActive={_toggleActive}
              />
            </div>
            <div className="hidden sm:block">
              <AdminAccountTable
                data={tableQuery?.data?.data}
                pageSize={tableQuery?.data?.pageSize}
                currentPage={tableQuery?.data?.paging.current_page}
                _toggleActive={_toggleActive}
              />
            </div>
          </>
        ) : <></>}
        {tableQuery?.data?.paging ? (
          <div className="mt-6">
            <Pagination
              currentPage={tableQuery?.data?.paging.current_page}
              totalPage={tableQuery?.data?.paging.total_page}
              total={{
                totalItems: tableQuery?.data?.paging?.total_items ?? 0,
                total: tableQuery?.data?.paging.total
              }}
              onChange={_handlePageChanged}
              hideTotal
            />
          </div>
        ) : <></>}
      </div>
      {(tableQuery.isLoading) || (tableQuery.isFetching && tableQuery.isPreviousData) || updateMutationIsLoading ? <FullScreenLoading /> : <></>}
    </div>
  )
}